import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';
import { ProjectStats } from 'src/shared/types';

import {
    createCustomerMutation,
    CreateCustomerParams,
    CreateCustomerResult,
    updateCustomerMutation,
    UpdateCustomerParams,
    UpdateCustomerResult,
} from './queries/customerAdminQuery';
import {
    buildCreateProjectVariables,
    createProjectMutation,
    CreateProjectParams,
    CreateProjectResult,
    customerProjectDefinitionsQuery,
    CustomerProjectDefinitionsParams,
    CustomerProjectDefinitionsResult,
    ProjectDefinitionListRow,
    projectStatsQuery,
    ProjectStatsParams,
    ProjectStatsResult,
    updateProjectMutation,
    UpdateProjectParams,
    UpdateProjectResult,
} from './queries/projectAdminQuery';

const adminGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        createCustomer: builder.mutation<CreateCustomerResult, CreateCustomerParams>({
            query: params => ({
                body: createCustomerMutation,
                variables: params,
            }),
            // Refreshes the organisations list (customers query) once a new org is created.
            invalidatesTags: ['customer'],
        }),

        updateCustomer: builder.mutation<UpdateCustomerResult, UpdateCustomerParams>({
            query: params => ({
                body: updateCustomerMutation,
                variables: params,
            }),
            // The org list and org page both read the name from the customers query.
            invalidatesTags: ['customer'],
        }),

        createProject: builder.mutation<CreateProjectResult, CreateProjectParams>({
            query: params => ({
                body: createProjectMutation,
                variables: buildCreateProjectVariables(params),
            }),
            // Refreshes the graph project list, and the customer's projectCount stat.
            invalidatesTags: ['project', 'customer'],
        }),

        customerProjectDefinitions: builder.query<ProjectDefinitionListRow[], CustomerProjectDefinitionsParams>({
            query: params => ({
                body: customerProjectDefinitionsQuery,
                variables: params,
            }),
            transformResponse: (response: CustomerProjectDefinitionsResult) =>
                (response.customer?.basicProjectList ?? []).filter((row): row is ProjectDefinitionListRow => Boolean(row)),
            providesTags: ['project'],
        }),

        updateProject: builder.mutation<UpdateProjectResult, UpdateProjectParams>({
            query: params => ({
                body: updateProjectMutation,
                variables: { projectId: params.projectId, projectName: params.projectName.trim() },
            }),
            // The projects table reads the name from the graph list.
            invalidatesTags: ['project'],
        }),

        projectStats: builder.query<ProjectStats | null, ProjectStatsParams>({
            query: params => ({
                body: projectStatsQuery,
                variables: params,
            }),
            transformResponse: (response: ProjectStatsResult) => response.project?.stats ?? null,
            providesTags: ['project'],
        }),
    }),
});

export const {
    useCreateCustomerMutation,
    useUpdateCustomerMutation,
    useCustomerProjectDefinitionsQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useProjectStatsQuery,
} = adminGraphSlice;
